import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal } from 'antd';

import { isTokenValid } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { cardPagePath } from '../../utils/cards';
import { readRecents } from '../../utils/recents';
import { useOnSessionChange } from '../../utils/session';
import { fetchRecentViews } from '../../services/views';
import useResolvedViewHistory from '../../hooks/useResolvedViewHistory';
import useTagSearch from '../../hooks/useTagSearch';
import useCombinedListMatches from '../../hooks/useCombinedListMatches';
import { combinedListPath } from '../../utils/combinedLists';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollections } from '../../contexts/CollectionsContext';
import { parentKicker } from '../../utils/collectionFamily';
import { owningCollectionIdOf } from '../../utils/recordQualifier';
import RecordBadge from './RecordBadge';
import TagBadge from '../tags/TagBadge';
import { tagPagePath } from '../../utils/tags';
import { collectionSearchPath } from '../../utils/collectionSearchPaths';
import { ACTIVITY, CARD_ICON, CLOCK, COLLECTION_ICON, EDIT, LAYERS, MAGNIFYING_GLASS, PLUS } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { DataState, Skeleton, deriveDataState } from '../loading';
import useItemSearch from '../../hooks/useItemSearch';
import SearchSnippet, { hasSnippetText, isTitleSnippet } from '../search/SearchSnippet';
import getItemDisplayValue from '../../utils/items';

/** Case-insensitive subsequence match, ranked: substring beats scatter. */
const fuzzyScore = (query, text) => {
	const q = query.toLowerCase();
	const t = (text || '').toLowerCase();
	if (!q) return 1;
	const direct = t.indexOf(q);
	if (direct >= 0) return 1000 - direct;
	let ti = 0;
	for (let qi = 0; qi < q.length; qi += 1) {
		ti = t.indexOf(q[qi], ti);
		if (ti < 0) return 0;
		ti += 1;
	}
	return 10;
};

// The row's kind, in the words the product uses for each kind of tag (ruled d01).
const TAG_KIND_WORDS = { private: 'Private tag', collection: 'Collection tag', item: 'Item tag' };

/** Where an item opens from the palette: its collection's list with it in the pane (the recent-item path). */
export const paletteItemPath = (collectionId, itemId) => `/collections/${encodeURIComponent(collectionId)}/items?item=${encodeURIComponent(itemId)}`;

/** The item a row's path opens (`?item=<id>`, or `/items/<id>`), so a hit can yield to a recent row for it. */
export function itemIdOfPath(path) {
	const text = `${path || ''}`;
	const query = /[?&]item=([^&#]+)/.exec(text);
	if (query) return decodeURIComponent(query[1]);
	const segment = /\/items\/([^/?#]+)/.exec(text);
	return segment ? decodeURIComponent(segment[1]) : null;
}

/**
 * The global ⌘K palette: fuzzy-jump to any collection or card, with the
 * server-side view history (recently viewed collections, cards, AND items)
 * as the empty-query state and as an extra bucket typed queries match. The
 * localStorage recents remain only as a fallback while history is loading
 * or unavailable.
 *
 * `actions` are verbs rather than places — `{ key, title, run }`, drawn as an
 * **Actions** row with a + glyph and run instead of navigated to. With an
 * empty query they lead the list (the shell's "Add item to {your personal
 * collection}" is the first row); a typed query matches them like anything
 * else, and they win a tie. An action marked `onlyWhenTyped` waits for a
 * query — the Home launchers past the first few, and every favorite card
 * ("Add Blood pressure to Foster home"), would otherwise bury the history.
 *
 * TAGS (tags T5): a typed query also asks the server's tag search
 * (`GET /api/tags/search`, hooks/useTagSearch — settled for 250 ms, answers kept
 * per session): the person's private tags, the collection tags they can read
 * and item tags found by their item's title, each a row with its kind
 * ("Private tag", "Collection tag", "Item tag") that opens its TAG PAGE. They
 * rank with everything else by how well the name matches (a tag found by an
 * alias still makes the list, at the bottom).
 *
 * ITEMS BY THEIR WORDS (KPR-1706): from two characters, the palette also asks
 * global search for items (`GET /api/search?types=items`, hooks/useItemSearch —
 * settled 250 ms, a small page, the newest term's answer only). The hits are
 * their own block AFTER the ranked rows and before "Search Discover" and
 * "Search in <collection>", in the server's order (ranked by the words, KPR-490):
 * each the item's record badge with its snippet on a second line (the matched
 * words marked; not when the snippet is the title the badge shows). A recent
 * item already listed wins over its own hit. The block's waits go through
 * DataState; a refusal of the term is one line in the server's words; neither
 * ever hides the other rows.
 */
export default function CommandPalette({ open, onClose, actions = [], collectionScope = null }) {
	const navigate = useNavigate();
	const { cards, cardsById, getElementsForCard } = useCardDefinitions();
	const [query, setQuery] = useState('');
	const [activeIndex, setActiveIndex] = useState(0);
	// The shared collections list (contexts/CollectionsContext). `collections`
	// stays null until it has answered, and when the read failed the palette
	// says so (with a Retry) instead of "No matches."
	const { collections: loadedCollections, available: collectionsLoaded, error: collectionsError, refresh: refreshCollections } = useCollections();
	const collections = collectionsLoaded ? loadedCollections : null;
	const collectionsFailed = !collectionsLoaded && Boolean(collectionsError);
	const [historyViews, setHistoryViews] = useState(null);
	const inputRef = useRef(null);

	// The palette lives in the shell and stays mounted through a sign-out and
	// the next sign-in: its history is the account's, so another account (or
	// none) starts it over (its collections are the shared list's).
	const session = useOnSessionChange(() => {
		setHistoryViews(null);
	});

	// History refreshes on every open — it changes with every navigation.
	useEffect(() => {
		if (!open || !isTokenValid()) return;
		let active = true;
		fetchRecentViews(12)
			.then(views => { if (active) setHistoryViews(views); })
			.catch(() => { if (active) setHistoryViews([]); });
		return () => { active = false; };
	}, [open, session]);

	const historyRows = useResolvedViewHistory(historyViews);

	// Tags come from the server as the person types (never with an empty query).
	const { results: tagResults } = useTagSearch(query, { enabled: open && isTokenValid() });
	// The person's saved combined lists (KPR-1022), found by name; read once
	// a session, and only once the palette has been opened.
	const { lists: combinedLists } = useCombinedListMatches({ enabled: open && isTokenValid() });
	// Items by their words (KPR-1706), from two characters.
	const itemSearch = useItemSearch(query, { enabled: open && isTokenValid() });

	useEffect(() => {
		if (open) {
			setQuery('');
			setActiveIndex(0);
			// After the modal's own focus dance.
			setTimeout(() => inputRef.current?.focus(), 50);
		}
	}, [open]);

	// The server history as palette rows; falls back to localStorage recents
	// while history hasn't loaded (or came back empty on an older server).
	const recentRows = useMemo(() => {
		if (historyRows.length) {
			return historyRows.map(row => ({
				key: row.key,
				kind: `Recent ${row.kind.toLowerCase()}`,
				title: row.title,
				path: row.path,
				record: row.card,
				recordKind: row.targetType,
				owner: row.owner || null,
				itemId: row.targetType === 'item' ? row.id : null,
			}));
		}
		return readRecents().map(entry => ({
			key: `recent-${entry.path}`,
			kind: entry.type === 'item' ? 'Recent item' : `Recent ${entry.type}`,
			title: entry.title,
			path: entry.path,
			record: null,
			itemId: entry.type === 'item' ? itemIdOfPath(entry.path) : null,
		}));
	}, [historyRows]);

	const historyLink = useMemo(() => ({
		key: 'view-all-history',
		kind: 'Recently viewed',
		title: 'View everything recently viewed',
		path: '/history',
		record: null,
		isHistoryLink: true,
	}), []);

	// Pages that are not a collection or a card, found by typing their name:
	// Drafts and Status, each the account menu's other door to it.
	const pageRows = useMemo(() => ([
		{ key: 'page-drafts', kind: 'Page', title: 'Drafts', match: 'Drafts unsaved items', path: '/drafts', record: null, isPage: true, icon: EDIT },
		// KPR-1178: whether keepr is up, its versions and recent changes.
		{ key: 'page-status', kind: 'Page', title: 'Status', match: 'Status uptime is keepr up down versions', path: '/status', record: null, isPage: true, icon: ACTIVITY },
	]), []);

	const actionRows = useMemo(() => (Array.isArray(actions) ? actions : [])
		.filter(action => action && action.key && action.title && typeof action.run === 'function')
		.map(action => ({
			key: `action-${action.key}`,
			kind: 'Action',
			title: action.title,
			run: action.run,
			record: null,
			isAction: true,
			onlyWhenTyped: Boolean(action.onlyWhenTyped),
		})), [actions]);

	// The rows in three runs: the ranked rows (`head`), the item hits, and the
	// searches that always close the list (`tail`). Keyboard moves run through
	// the three as one list.
	const { head, tail } = useMemo(() => {
		const trimmed = query.trim();
		if (!trimmed) {
			return { head: [...actionRows.filter(row => !row.onlyWhenTyped), ...recentRows, historyLink], tail: [] };
		}
		const scored = [];
		actionRows.forEach(row => {
			const score = fuzzyScore(trimmed, row.title);
			// +2: an action outranks a collection or card on a tie.
			if (score > 0) scored.push({ ...row, score: score + 2 });
		});
		pageRows.forEach(row => {
			const score = fuzzyScore(trimmed, row.title) || (fuzzyScore(trimmed, row.match) >= 900 ? 900 : 0);
			if (score > 0) scored.push({ ...row, score: score + 1 });
		});
		(combinedLists || []).forEach(list => {
			const score = fuzzyScore(trimmed, list?.name);
			if (list?._id && score > 0) {
				scored.push({
					key: `combined-list-${list._id}`,
					kind: 'Combined list',
					title: list.name || 'Combined list',
					path: combinedListPath(list._id),
					record: null,
					isCombinedList: true,
					score: score + 1,
				});
			}
		});
		(collections || []).forEach(collection => {
			const id = normalizeId(collection?._id);
			// A sub-collection answers to its parent's name too — "biewend
			// health" finds Health, and so does "health".
			const parentName = parentKicker(collection);
			const score = Math.max(
				fuzzyScore(trimmed, collection?.name),
				parentName ? fuzzyScore(trimmed, `${parentName} ${collection?.name || ''}`) : 0
			);
			if (id && score > 0) {
				scored.push({
					key: `collection-${id}`,
					kind: 'Collection',
					title: collection?.name || 'Untitled collection',
					path: `/collections/${id}/items`,
					record: collection,
					recordKind: 'collection',
					score: score + 1, // collections outrank cards on ties
				});
			}
		});
		(cards || []).forEach(card => {
			const id = normalizeId(card?._id);
			const score = fuzzyScore(trimmed, card?.name || card?.label?.singular);
			if (id && score > 0) {
				scored.push({
					key: `card-${id}`,
					kind: 'Card',
					title: card?.name || card?.label?.singular || 'Untitled card',
					path: cardPagePath(card),
					record: card,
					recordKind: 'card',
					owner: owningCollectionIdOf(card, 'card'),
					score,
				});
			}
		});
		(tagResults || []).forEach(result => {
			const tag = result?.tag;
			const path = tagPagePath(tag);
			if (!path) return;
			const name = result.kind === 'item' ? (tag.title || 'Untitled item') : (tag.name || 'Tag');
			// The server matched the name or an alias; an alias-only match
			// still belongs in the list, below any real name match.
			const score = Math.max(fuzzyScore(trimmed, name), 5);
			scored.push({
				key: `tag-${normalizeId(tag._id)}`,
				kind: TAG_KIND_WORDS[result.kind] || 'Tag',
				title: name,
				path,
				record: null,
				tagResult: result,
				score,
			});
		});
		// History matches too — it's the only door to items — but a
		// collection/card already matched above wins over its history echo.
		const claimedPaths = new Set(scored.map(row => row.path));
		recentRows.forEach(row => {
			const score = fuzzyScore(trimmed, row.title);
			if (score > 0 && !claimedPaths.has(row.path)) {
				scored.push({ ...row, score });
			}
		});
		const ranked = scored.sort((a, b) => b.score - a.score).slice(0, 12);
		const closing = [];
		// KPR-1437: the words can be searched on Discover too, best match first —
		// once the collections have answered, so a slow read still shows its
		// skeleton and a failed one its Retry rather than this lone row. Before
		// the search-in-collection entry, which stays last.
		if (collections) {
			closing.push({
				key: 'search-discover',
				kind: 'Discover',
				title: `Search Discover for \u201c${trimmed}\u201d`,
				path: `/discover?${new URLSearchParams({ search: trimmed }).toString()}`,
				record: null,
				isSearch: true,
			});
		}
		// Inside a collection, the typed term can be searched there (KPR-356).
		// Appended after the global results, so their order and cut are unchanged.
		if (trimmed && collectionScope?.id && collectionScope?.name) {
			closing.push({
				key: `search-in-${collectionScope.id}`,
				kind: 'Search',
				title: `Search in ${collectionScope.name} for \u201c${trimmed}\u201d`,
				path: collectionSearchPath(collectionScope.id, trimmed),
				record: null,
				isSearch: true,
			});
		}
		return { head: ranked, tail: closing };
	}, [query, collections, cards, recentRows, historyLink, actionRows, pageRows, combinedLists, tagResults, collectionScope]);

	// The items found by their words, in the server's order (KPR-1706). A
	// recent item already among the ranked rows is that item's row; its hit
	// is not listed twice.
	const hitRows = useMemo(() => {
		if (!itemSearch.active || !itemSearch.hits.length) return [];
		const listed = new Set(head.map(row => row.itemId).filter(Boolean));
		return itemSearch.hits
			.filter(hit => !listed.has(hit._id))
			.map((hit) => {
				const card = cardsById?.[hit.card_id] || null;
				// The title as the server may show it to this reader (KPR-32);
				// an API from before KPR-490 sends none, so the card's display
				// template stands in, as a recent row's does.
				const served = typeof hit.displayValue === 'string' && hit.displayValue.trim() ? hit.displayValue : '';
				const title = served || getItemDisplayValue({
					card,
					elementDefs: card && getElementsForCard ? getElementsForCard(hit.card_id) : [],
					item: hit,
				}) || 'Untitled item';
				return {
					key: `item-hit-${hit._id}`,
					kind: 'Item',
					title,
					path: paletteItemPath(hit.collection_id, hit._id),
					record: card || {},
					recordKind: 'item',
					owner: hit.collection_id,
					itemId: hit._id,
					snippet: hit.snippet,
					isItemHit: true,
				};
			});
	}, [itemSearch.active, itemSearch.hits, head, cardsById, getElementsForCard]);

	const results = useMemo(() => [...head, ...hitRows, ...tail], [head, hitRows, tail]);

	useEffect(() => {
		setActiveIndex(0);
	}, [results.length, query]);

	// The highlighted row stays in sight as Up and Down walk past the list's
	// fold (the item hits sit low in it). jsdom has no scrollIntoView.
	const listRef = useRef(null);
	useEffect(() => {
		const row = listRef.current?.querySelector('[role="option"][aria-selected="true"]');
		if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
	}, [activeIndex, results.length]);

	// The destination records its own view (recordView on the collection
	// layout, item pane, item form, and card form), so navigation is all
	// that's left to do here.
	const go = (result) => {
		if (!result) return;
		onClose();
		if (typeof result.run === 'function') {
			result.run();
			return;
		}
		navigate(result.path);
	};

	const handleKeyDown = (event) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActiveIndex(index => Math.min(results.length - 1, index + 1));
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActiveIndex(index => Math.max(0, index - 1));
		} else if (event.key === 'Enter') {
			event.preventDefault();
			go(results[activeIndex]);
		}
	};

	// One row of any run. `index` is its place in the one list the keys walk.
	const renderRow = (result, index) => {
		// A hit's snippet, unless it quotes the title the badge already shows.
		const snippet = result.isItemHit && result.snippet && !isTitleSnippet(result.snippet) && hasSnippetText(result.snippet)
			? result.snippet
			: null;
		return (
			<button
				type="button"
				key={result.key}
				role="option"
				aria-selected={index === activeIndex}
				className={`command-palette__row${snippet ? ' command-palette__row--snippet' : ''}${index === activeIndex ? ' command-palette__row--active' : ''}`}
				onMouseEnter={() => setActiveIndex(index)}
				onClick={() => go(result)}
			>
				{result.tagResult ? (
					// A tag is its chip: a private tag muted with the `user`
					// glyph, an item tag its item's badge (the card's tile and
					// the title, qualified by the collection it lives in).
					<TagBadge
						tag={result.tagResult.tag}
						size="xs"
						className="command-palette__row-badge"
						resolveItemTag={result.tagResult.kind === 'item' ? () => ({
							record: cardsById?.[normalizeId(result.tagResult.tag.card_id)] || null,
							title: result.title,
							collection: result.tagResult.collection?._id || result.tagResult.tag.collection_id || null,
						}) : undefined}
					/>
				) : result.record ? (
					<RecordBadge
						kind={result.recordKind || 'card'}
						record={result.record}
						// An item's badge says its card; a sub-collection's
						// says its parent (so two "Health"s never read
						// alike); a card's or a collection's says only
						// itself.
						kicker={result.recordKind === 'item' ? result.record?.name
							: result.recordKind === 'collection' ? (parentKicker(result.record) || undefined)
							: undefined}
						title={result.title}
						size="xs"
						className="command-palette__row-badge"
						// The palette is every collection's: an item or a
						// collection's card carries where it lives.
						collection={result.owner || null}
					/>
				) : (
					<>
						<span className="command-palette__row-icon">
							{result.isAction ? <PLUS />
								: result.isHistoryLink ? <CLOCK />
								: result.isPage ? React.createElement(result.icon || EDIT)
								: result.isCombinedList ? <LAYERS />
								: result.isSearch ? <MAGNIFYING_GLASS />
								: result.kind.toLowerCase().includes('card') ? <CARD_ICON />
								: <COLLECTION_ICON />}
						</span>
						<span className="command-palette__row-title">{result.title}</span>
					</>
				)}
				<span className="command-palette__row-spacer" />
				<span className="command-palette__row-kind">{result.kind}</span>
				{snippet ? <SearchSnippet snippet={snippet} className="command-palette__snippet" /> : null}
			</button>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			closable={false}
			className="command-palette"
			width={560}
			destroyOnClose
		>
			<div className="command-palette__input-row">
				<MAGNIFYING_GLASS aria-hidden="true" />
				<Input
					{...NO_TEXT_ASSIST}
					ref={inputRef}
					variant="borderless"
					placeholder="Jump anywhere, find items by their words, or add an item…"
					value={query}
					onChange={event => setQuery(event.target.value)}
					onKeyDown={handleKeyDown}
					aria-label="Search"
				/>
			</div>
			<div className="command-palette__results" role="listbox" ref={listRef}>
				{head.length + tail.length === 0 && !hitRows.length ? (
					collectionsFailed ? (
						<div className="command-palette__empty" role="alert">
							Unable to load your collections.{' '}
							<Button size="small" onClick={() => refreshCollections()}>Retry</Button>
						</div>
					) : collections === null && isTokenValid() ? (
						<div className="command-palette__empty">
							<Skeleton variant="list-row" count={3} />
						</div>
					) : itemSearch.active ? null : (
						<div className="command-palette__empty">
							{query.trim() ? 'No matches.' : 'Nothing visited yet — start typing.'}
						</div>
					)
				) : null}
				{head.map((result, index) => renderRow(result, index))}
				{itemSearch.active ? (
					// Items by their words (KPR-1706). Its own waits, never the
					// other rows': skeleton rows on a first answer, the earlier
					// hits under the refresh cue while a newer term is asked, a
					// failure with Retry (over the earlier hits, when there are any).
					<DataState
						className="command-palette__items"
						role="group"
						aria-label="Items"
						state={deriveDataState({ loading: itemSearch.loading, error: itemSearch.error, hasData: itemSearch.answered })}
						hasData={itemSearch.answered}
						skeleton={<Skeleton variant="list-row" count={2} />}
						error={itemSearch.error ? itemSearch.error.message : null}
						onRetry={itemSearch.retry}
						label="items"
					>
						{itemSearch.refusal ? (
							<p className="command-palette__notice">{`Items could not be searched. ${itemSearch.refusal}`}</p>
						) : null}
						{hitRows.map((result, index) => renderRow(result, head.length + index))}
					</DataState>
				) : null}
				{tail.map((result, index) => renderRow(result, head.length + hitRows.length + index))}
			</div>
		</Modal>
	);
}
